import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAnalytics } from '../contexts/AnalyticsContext';

const { FiFilter, FiTrendingDown, FiChevronRight, FiClock, FiUsers, FiActivity, FiTarget } = FiIcons;

const FunnelAnalysis = () => {
  const { analyticsData, customUsers } = useAnalytics();
  const [selectedUserList, setSelectedUserList] = useState(null);

  const funnels = [
    {
      id: 'conversion',
      name: 'Neural Conversion Path',
      description: 'Primary customer acquisition journey from landing to contact.',
      steps: [
        { label: 'Intelligence Landing', path: '/' },
        { label: 'Product Discovery', path: '/products' },
        { label: 'Pricing Evaluation', path: '/pricing' },
        { label: 'Lead Generation', path: '/contact' }
      ]
    },
    {
      id: 'engagement',
      name: 'Content Velocity',
      description: 'Measures how deeply users engage with platform documentation.',
      steps: [
        { label: 'Home', path: '/' },
        { label: 'Research Blog', path: '/blog' },
        { label: 'Feature Deep-dive', path: '/features' }
      ]
    }
  ];

  const [activeFunnel, setActiveFunnel] = useState(funnels[0]);

  const funnelMetrics = useMemo(() => {
    const sessions = {};
    (analyticsData.pageViews || []).forEach(pv => {
      if (!sessions[pv.sessionId]) sessions[pv.sessionId] = [];
      sessions[pv.sessionId].push(pv);
    });

    const metrics = [];
    
    for (let index = 0; index < activeFunnel.steps.length; index++) {
      const step = activeFunnel.steps[index];
      const usersAtStep = [];
      const timeToStep = [];

      Object.values(sessions).forEach(sessionViews => {
        const sortedViews = [...sessionViews].sort((a, b) => a.timestamp - b.timestamp);
        const paths = sortedViews.map(v => v.page);

        // Check progression sequentially
        let reachedThisStep = true;
        for (let i = 0; i <= index; i++) {
          if (!paths.includes(activeFunnel.steps[i].path)) {
            reachedThisStep = false;
            break;
          }
        }

        if (reachedThisStep) {
          const viewAtStep = sortedViews.find(v => v.page === step.path);
          if (viewAtStep) {
            const userMeta = customUsers.find(u => u.user_serial === viewAtStep.userSerial || u.linked_visitor_ids?.includes(viewAtStep.visitorId));
            usersAtStep.push({
              id: viewAtStep.userSerial || viewAtStep.visitorId,
              name: userMeta ? `${userMeta.first_name} ${userMeta.last_name}` : 'Anonymous Identity',
              timestamp: viewAtStep.timestamp,
              isIdentified: !!userMeta
            });

            if (index > 0) {
              const prevStepPath = activeFunnel.steps[index - 1].path;
              const prevView = sortedViews.find(v => v.page === prevStepPath);
              if(prevView) {
                timeToStep.push(viewAtStep.timestamp - prevView.timestamp);
              }
            }
          }
        }
      });

      const count = usersAtStep.length;
      const prevCount = index > 0 ? metrics[index - 1].count : count;

      metrics.push({
        ...step,
        count,
        users: usersAtStep,
        avgTime: timeToStep.length > 0 ? Math.floor(timeToStep.reduce((a, b) => a + b, 0) / timeToStep.length) : 0,
        dropOff: index > 0 && prevCount > 0 ? ((prevCount - count) / prevCount * 100).toFixed(1) : 0,
        conversion: index > 0 && metrics[0]?.count > 0 ? (count / metrics[0].count * 100).toFixed(1) : 100
      });
    }

    return metrics;
  }, [activeFunnel, analyticsData.pageViews, customUsers]);

  const totalConversion = funnelMetrics.length > 0 && funnelMetrics[0].count > 0 
    ? ((funnelMetrics[funnelMetrics.length - 1].count / funnelMetrics[0].count) * 100).toFixed(1) 
    : "0.0";

  return (
    <div className="space-y-10">
      {/* Funnel Header */}
      <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm flex flex-col lg:flex-row justify-between items-start lg:items-center gap-8">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600">
              <SafeIcon icon={FiFilter} className="text-xl" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900 tracking-tight">{activeFunnel.name}</h2>
              <p className="text-slate-500 font-medium text-xs mt-0.5">{activeFunnel.description}</p>
            </div>
          </div>
          <div className="flex gap-2">
            {funnels.map(f => (
              <button 
                key={f.id} 
                onClick={() => setActiveFunnel(f)}
                className={`px-4 py-2 rounded-lg text-xs font-bold transition-all shadow-sm ${
                  activeFunnel.id === f.id 
                    ? 'bg-slate-900 text-white' 
                    : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                {f.name}
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-8 bg-slate-50 p-6 rounded-xl border border-slate-100">
          <div className="text-right">
            <div className="text-3xl font-black text-blue-600">{totalConversion}%</div>
            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Global Efficiency</div>
          </div>
          <div className="w-px bg-slate-200"></div>
          <div className="text-right">
            <div className="text-3xl font-black text-slate-900">{funnelMetrics[0]?.count || 0}</div>
            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Entry Volume</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Funnel visualization */}
        <div className="lg:col-span-3 space-y-4">
          {funnelMetrics.map((step, idx) => (
            <div key={step.path} className="relative">
              <motion.div 
                initial={{ opacity: 0, x: -20 }} 
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm hover:border-blue-300 transition-colors group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-slate-50 rounded-lg flex items-center justify-center text-slate-500 font-bold text-sm border border-slate-100 group-hover:bg-blue-50 group-hover:text-blue-600 transition-colors">
                      0{idx + 1}
                    </div>
                    <div>
                      <h4 className="text-base font-bold text-slate-900">{step.label}</h4>
                      <p className="text-xs font-mono text-slate-500 mt-0.5">{step.path}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-8">
                    {idx > 0 && (
                      <div className="text-right hidden sm:block">
                        <div className="flex items-center justify-end gap-1.5 text-slate-900 font-bold text-sm">
                          <SafeIcon icon={FiClock} className="text-blue-500" />
                          {step.avgTime > 1000 ? `${(step.avgTime / 1000).toFixed(1)}s` : `${step.avgTime}ms`}
                        </div>
                        <div className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Avg Velocity</div>
                      </div>
                    )}
                    <div className="text-right min-w-[80px]">
                      <div className="text-2xl font-black text-slate-900">{step.count}</div>
                      <button 
                        onClick={() => setSelectedUserList(step)}
                        className="text-[9px] font-bold text-blue-600 uppercase tracking-widest hover:underline flex items-center justify-end gap-1 mt-0.5 w-full"
                      >
                        <SafeIcon icon={FiUsers} /> View Cohort
                      </button>
                    </div>
                  </div>
                </div>

                {/* Progress Visual */}
                <div className="mt-5 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <motion.div 
                    initial={{ width: 0 }}
                    animate={{ width: `${(step.count / (funnelMetrics[0]?.count || 1)) * 100}%` }}
                    className="h-full bg-blue-500 rounded-full"
                  />
                </div>
              </motion.div>

              {/* Drop-off Bridge */}
              {idx < funnelMetrics.length - 1 && (
                <div className="py-4 flex justify-center relative">
                  <div className="absolute left-1/2 -translate-x-1/2 h-full w-px bg-slate-200"></div>
                  <div className="relative z-10 flex items-center gap-2 bg-white text-rose-500 px-4 py-1.5 rounded-full border border-slate-200 shadow-sm text-xs font-bold">
                    <SafeIcon icon={FiTrendingDown} />
                    <span>{step.dropOff}% Churn</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Intelligence Sidebar */}
        <div className="space-y-6">
          <div className="bg-slate-900 rounded-2xl p-6 text-white shadow-xl">
            <h3 className="text-base font-bold mb-5 flex items-center gap-2">
              <SafeIcon icon={FiActivity} className="text-blue-400" /> Neural Insights
            </h3>
            
            <div className="space-y-4">
              <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                <div className="text-[10px] font-bold text-blue-400 uppercase tracking-widest mb-1">Primary Bottleneck</div>
                <div className="font-bold text-sm">
                  {funnelMetrics.length > 1 ? funnelMetrics.reduce((prev, curr) => parseFloat(curr.dropOff) > parseFloat(prev.dropOff) ? curr : prev).label : 'N/A'}
                </div>
                <p className="text-xs text-slate-400 mt-1.5">Highest volume of session abandonment detected at this node.</p>
              </div>
            </div>
            
            <button className="w-full mt-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg text-xs font-bold uppercase tracking-widest transition-all shadow-sm">
              Run Simulation
            </button>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
            <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center text-indigo-600 mb-4">
              <SafeIcon icon={FiTarget} />
            </div>
            <h4 className="font-bold text-slate-900 text-sm mb-2">Strategy Recommendation</h4>
            <p className="text-xs text-slate-500 leading-relaxed">
              Based on the drop-off metrics, consider implementing a "Compare Features" modal at the pricing stage to reduce friction and improve velocity.
            </p>
          </div>
        </div>
      </div>

      {/* User Cohort Modal */}
      <AnimatePresence>
        {selectedUserList && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }} 
              animate={{ opacity: 1, scale: 1 }} 
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden flex flex-col max-h-[85vh]"
            >
              <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                <div>
                  <h3 className="text-xl font-bold text-slate-900 tracking-tight">Step Cohort</h3>
                  <p className="text-slate-500 text-xs mt-0.5">Users who reached: <span className="font-bold">{selectedUserList.label}</span></p>
                </div>
                <button 
                  onClick={() => setSelectedUserList(null)} 
                  className="p-2 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiIcons.FiX} className="text-xl" />
                </button>
              </div>
              
              <div className="p-6 overflow-y-auto custom-scrollbar flex-1">
                <div className="space-y-3">
                  {selectedUserList.users.length === 0 ? (
                     <div className="text-center py-8 text-slate-500 text-sm">No users reached this step.</div>
                  ) : (
                    selectedUserList.users.map((user, idx) => (
                      <Link 
                        key={idx} 
                        to={`/user/${user.id}`} 
                        className="flex items-center justify-between p-4 bg-white hover:bg-slate-50 rounded-xl border border-slate-200 transition-all group"
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm ${user.isIdentified ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-500'}`}>
                            {user.name[0]}
                          </div>
                          <div>
                            <div className="font-bold text-slate-900 text-sm group-hover:text-blue-600 transition-colors">{user.name}</div>
                            <div className="text-[10px] font-mono text-slate-500">{user.id.substring(0, 16)}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Entry Time</div>
                            <div className="text-xs font-medium text-slate-900">{new Date(user.timestamp).toLocaleTimeString()}</div>
                          </div>
                          <SafeIcon icon={FiChevronRight} className="text-slate-300 group-hover:text-blue-600 transition-colors" />
                        </div>
                      </Link>
                    ))
                  )}
                </div>
              </div>
              <div className="p-4 bg-slate-50 border-t border-slate-100 text-center">
                <p className="text-xs text-slate-500 font-medium">Displaying {selectedUserList.users.length} unique interactions</p>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FunnelAnalysis;